import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Clock, Loader2, ShieldAlert, Send, X } from "lucide-react";

const STATUS_COPY = {
  reported: { label: "No-show reported", cls: "bg-amber-100 text-amber-800" },
  cleaner_responded: { label: "Cleaner responded", cls: "bg-blue-100 text-blue-800" },
  cleaner_cannot_attend: { label: "Cleaner can't attend", cls: "bg-orange-100 text-orange-800" },
  escalated: { label: "Escalated", cls: "bg-red-100 text-red-700" },
  final_dispute: { label: "Final dispute period", cls: "bg-red-100 text-red-700" },
  admin_review: { label: "Under review", cls: "bg-violet-100 text-violet-700" },
  confirmed: { label: "Confirmed no-show", cls: "bg-red-600 text-white" },
  dismissed: { label: "Report dismissed", cls: "bg-emerald-100 text-emerald-700" },
};

function Countdown({ until, label }) {
  const [left, setLeft] = useState(() => new Date(until) - new Date());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(until) - new Date()), 1000);
    return () => clearInterval(t);
  }, [until]);
  if (left <= 0) return <span className="text-sm text-muted-foreground">{label} elapsed</span>;
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  const h = Math.floor(m / 60);
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium" data-testid="noshow-timer">
      <Clock className="h-4 w-4 text-red-600" />
      {h > 0 ? `${h}h ${m % 60}m` : `${m}:${String(s).padStart(2, "0")}`} left to {label}
    </span>
  );
}

export function ReportNoShowButton({ booking, onReported }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/noshow/report", { booking_id: booking.booking_id, details });
      toast.success("Reported — we've alerted your cleaner and started the 15-minute response window.");
      setOpen(false);
      onReported();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="px-4 py-2 rounded-full border-2 border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 transition-colors inline-flex items-center gap-1.5" data-testid={`report-noshow-${booking.booking_id}`}>
        <AlertTriangle className="h-4 w-4" /> Report cleaner no-show
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:px-5" onClick={() => setOpen(false)}>
          <div className="bg-card rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="noshow-report-modal">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-xl font-semibold">Report a no-show</h3>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-secondary" data-testid="noshow-cancel"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <p className="font-semibold">This starts a formal no-show process</p>
              <p className="mt-1">
                Your cleaner is alerted immediately and has <strong>15 minutes</strong> to respond. Nothing is
                confirmed yet — if they don't reply the case is escalated, and they get a further 24 hours to
                dispute it with evidence before any no-show is confirmed. Please only report a genuine no-show.
              </p>
            </div>
            <textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What happened? (optional, shared with our team only)" className="w-full mt-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="noshow-details" />
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button onClick={submit} disabled={busy} className="flex-1 py-3 rounded-full bg-red-600 text-white font-medium hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="noshow-confirm-report">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Start no-show report
              </button>
              <button onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors">Keep waiting</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function NoShowCase({ case: c, role, options, onChanged }) {
  const [note, setNote] = useState("");
  const [dispute, setDispute] = useState({ explanation: "", arrival_info: "", evidence: "" });
  const [busy, setBusy] = useState(false);
  const copy = STATUS_COPY[c.status] || { label: c.status, cls: "bg-secondary" };
  const open = !["confirmed", "dismissed"].includes(c.status);
  const showDispute = role === "cleaner" && ["escalated", "final_dispute", "cleaner_cannot_attend", "cleaner_responded"].includes(c.status);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50/50 p-5" data-testid={`noshow-case-${c.case_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-red-600" />
        <p className="font-display font-semibold">{role === "cleaner" ? "URGENT — no-show reported" : "No-show report"}</p>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${copy.cls}`} data-testid={`noshow-status-${c.status}`}>{copy.label}</span>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        {c.service_name} · {c.date} at {c.start_time} · reported {new Date(c.reported_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
      </p>

      {role === "cleaner" && c.status === "reported" && (
        <div className="mt-3">
          <p className="text-sm">The customer has reported that you have not attended this booking. You have 15 minutes to respond and let us know what is happening.</p>
          <div className="mt-2"><Countdown until={c.respond_deadline} label="respond" /></div>
        </div>
      )}
      {role === "cleaner" && c.status === "final_dispute" && c.dispute_deadline && (
        <div className="mt-3"><Countdown until={c.dispute_deadline} label="dispute with evidence" /></div>
      )}
      {role === "customer" && c.status === "escalated" && (
        <p className="text-sm mt-3">Your cleaner didn't respond in 15 minutes, so we've escalated this. They have a final chance to respond before we confirm it.</p>
      )}
      {c.status === "admin_review" && <p className="text-sm mt-3">Our team is reviewing the evidence provided. We'll be in touch.</p>}
      {c.status === "confirmed" && (
        <p className="text-sm mt-3" data-testid="noshow-confirmed-note">
          Confirmed no-show. {role === "customer"
            ? `We're really sorry this happened. Your payment has been refunded in full and we've added a £${(c.goodwill_credit || 10).toFixed(2)} goodwill credit to your account towards your next clean.`
            : "A £10 no-show fee is outstanding on your account."}
        </p>
      )}
      {c.status === "dismissed" && <p className="text-sm mt-3">This report was dismissed. Nothing is recorded against the cleaner.</p>}

      {c.responses?.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="noshow-responses">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Response history</p>
          {c.responses.map((r, i) => (
            <p key={i} className="text-sm">
              <strong>{r.label}</strong>{r.note ? ` — ${r.note}` : ""}
              <span className="text-muted-foreground"> · {new Date(r.at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>
            </p>
          ))}
        </div>
      )}
      {c.disputes?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="noshow-disputes">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dispute submitted</p>
          {c.disputes.map((d, i) => (
            <p key={i} className="text-sm">{d.explanation}<span className="text-muted-foreground"> · {new Date(d.at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span></p>
          ))}
        </div>
      )}

      {role === "cleaner" && open && (
        <div className="mt-4 border-t border-red-200 pt-4">
          <p className="text-sm font-semibold">Respond now</p>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)" className="w-full mt-2 px-3 py-2 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="noshow-note" />
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {Object.entries(options || {}).map(([key, label]) => (
              <button key={key} disabled={busy}
                onClick={() => act(() => api.post(`/noshow/${c.case_id}/respond`, { response_type: key, note }), "Response recorded")}
                className="px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-left hover:border-accent transition-colors disabled:opacity-50"
                data-testid={`noshow-respond-${key}`}>{label}</button>
            ))}
          </div>

          {showDispute && (
            <div className="mt-4" data-testid="noshow-dispute-form">
              <p className="text-sm font-semibold">Dispute with evidence</p>
              <textarea rows={3} value={dispute.explanation} onChange={(e) => setDispute({ ...dispute, explanation: e.target.value })} placeholder="Explain what happened" className="w-full mt-2 px-3 py-2 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="dispute-explanation" />
              <input value={dispute.arrival_info} onChange={(e) => setDispute({ ...dispute, arrival_info: e.target.value })} placeholder="Arrival / completion details (e.g. arrived 10:05, finished 12:30)" className="w-full mt-2 px-3 py-2 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="dispute-arrival" />
              <input value={dispute.evidence} onChange={(e) => setDispute({ ...dispute, evidence: e.target.value })} placeholder="Evidence (photo link, timestamps, anything relevant)" className="w-full mt-2 px-3 py-2 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="dispute-evidence" />
              <button disabled={busy || !dispute.explanation.trim()}
                onClick={() => act(() => api.post(`/noshow/${c.case_id}/dispute`, dispute), "Sent to our team for review")}
                className="mt-3 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="dispute-submit">
                <Send className="h-4 w-4" /> Submit dispute for review
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function NoShowCases({ role }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/noshow/mine").then((r) => setData(r.data)).catch(() => setData({ cases: [] }));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!data) return null;
  const cases = data.cases || [];
  const credit = data.credit_balance || 0;
  if (cases.length === 0 && credit <= 0) return null;
  return (
    <div className="space-y-3" data-testid="noshow-cases">
      {role === "customer" && credit > 0 && (
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="goodwill-credit-banner">
          <strong>£{credit.toFixed(2)} goodwill credit</strong> — added after a confirmed no-show. We'll apply
          it to your next clean. Sorry again for the inconvenience.
        </div>
      )}
      {cases.map((c) => (
        <NoShowCase key={c.case_id} case={c} role={role} options={data.response_options} onChanged={load} />
      ))}
    </div>
  );
}
